/**
 * Clock-out choices (change request 2026-11). Every clock-out off a job carries a Work Performed
 * note, typed or dictated here; it travels in the same queued item as the clock-out, so offline
 * they can't be separated or reordered. Then: clock into another job (or shop time), Out for
 * Lunch, or Out for the Day.
 */
import { useState } from 'react';
import { WORK_NOTE_MIN, type ClockStatus } from '@wo/shared';
import { enqueueItem } from './queue';
import type { JobsPayload, MechJob } from './types';
import { Big, NoteBox, Sheet } from './ui';

const WORKABLE = ['ASSIGNED', 'IN_PROGRESS', 'ON_HOLD'];

/** What the person is on now, from the payload (older cached payloads have only activePunch). */
export function currentClock(p: JobsPayload): Pick<ClockStatus, 'state' | 'workOrderId' | 'number' | 'category' | 'categoryLabel' | 'since'> {
  if (p.activePunch) return { state: 'job', workOrderId: p.activePunch.workOrderId, number: p.activePunch.number, category: null, categoryLabel: null, since: p.activePunch.startAt };
  if (p.clock?.state === 'time') return p.clock;
  return { state: p.clock?.state === 'lunch' ? 'lunch' : 'out', workOrderId: null, number: null, category: null, categoryLabel: null, since: null };
}

export const categoriesOf = (p: JobsPayload) => p.clock?.categories ?? [];

/** Optimistic payload changes after a queued clock action (the server's version replaces them). */
export function afterClock(p: JobsPayload, next: { kind: 'job'; job: MechJob } | { kind: 'time'; key: string; label: string } | { kind: 'out'; lunch: boolean }, note?: { woId: number; body: string }): JobsPayload {
  const now = new Date().toISOString();
  const base: ClockStatus = p.clock ?? { state: 'out', since: null, workOrderId: null, number: null, category: null, categoryLabel: null, lastOutAt: null, lastOutReason: null, categories: [], hoursToday: 0 };
  const jobs = p.jobs.map((j) => {
    let out = j;
    if (note && j.id === note.woId) out = { ...out, notes: [...out.notes, { id: -Date.now(), kind: 'WORK_PERFORMED', body: note.body, by: p.me.name, at: now, correctsNoteId: null, resolved: null }] };
    if (next.kind === 'job' && j.id === next.job.id && (j.status === 'ASSIGNED' || j.status === 'ON_HOLD')) out = { ...out, status: 'IN_PROGRESS', holdReason: null };
    return out;
  });
  if (next.kind === 'job') return { ...p, jobs, activePunch: { workOrderId: next.job.id, number: next.job.number, startAt: now }, clock: { ...base, state: 'job', since: now, workOrderId: next.job.id, number: next.job.number, category: null, categoryLabel: null } };
  if (next.kind === 'time') return { ...p, jobs, activePunch: null, clock: { ...base, state: 'time', since: now, workOrderId: null, number: null, category: next.key, categoryLabel: next.label } };
  return { ...p, jobs, activePunch: null, clock: { ...base, state: next.lunch ? 'lunch' : 'out', since: null, workOrderId: null, number: null, category: null, categoryLabel: null, lastOutAt: now, lastOutReason: next.lunch ? 'LUNCH' : 'DAY' } };
}

/**
 * Clock out of whatever is open (a job or shop time) — or, with `target`, switch straight to it.
 * `noteRequired`: always for a job; for shop time when its category says so.
 */
export function ClockOutSheet({
  payload,
  userId,
  onClose,
  onLocal,
  target,
}: {
  payload: JobsPayload;
  userId: number;
  onClose: () => void;
  onLocal: (fn: (p: JobsPayload) => JobsPayload) => void;
  /** Switching into this job straight away (pressed Clock In on another job). */
  target?: MechJob;
}) {
  const cur = currentClock(payload);
  const cats = categoriesOf(payload);
  const onJob = cur.state === 'job' ? payload.jobs.find((j) => j.id === cur.workOrderId) : undefined;
  const curCat = cur.state === 'time' ? cats.find((c) => c.key === cur.category) : undefined;
  const noteRequired = cur.state === 'job' || Boolean(curCat?.noteRequired);
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState(false);
  const ok = !noteRequired || note.trim().length >= WORK_NOTE_MIN;
  const body = note.trim() || undefined;
  const noteFor = cur.state === 'job' && body ? { woId: cur.workOrderId!, body } : undefined;
  const what = cur.state === 'job' ? `#${cur.number}` : (cur.categoryLabel ?? 'shop time');

  const toJob = async (j: MechJob) => {
    await enqueueItem(userId, `Clock in #${j.number}`, { type: 'clock_in', workOrderId: j.id, note: body });
    onLocal((p) => afterClock(p, { kind: 'job', job: j }, noteFor));
    onClose();
  };
  const toTime = async (key: string, label: string) => {
    await enqueueItem(userId, `Clock in: ${label}`, { type: 'time_in', category: key, note: body });
    onLocal((p) => afterClock(p, { kind: 'time', key, label }, noteFor));
    onClose();
  };
  const out = async (reason: 'LUNCH' | 'DAY') => {
    await enqueueItem(userId, `${reason === 'LUNCH' ? 'Out for lunch' : 'Out for the day'} (${what})`, { type: 'clock_out', note: body, reason });
    onLocal((p) => afterClock(p, { kind: 'out', lunch: reason === 'LUNCH' }, noteFor));
    onClose();
  };

  const others = payload.jobs.filter((j) => WORKABLE.includes(j.status) && j.id !== cur.workOrderId);
  const otherCats = cats.filter((c) => c.key !== cur.category);

  return (
    <Sheet title={target ? `Switch to #${target.number}` : `Clock out of ${what}`} onClose={onClose}>
      {onJob && <p className="mb-2 text-lg">{onJob.customer.name} · {onJob.machine.make} {onJob.machine.model}</p>}
      <p className="mb-2 text-lg font-semibold">
        {noteRequired ? `What did you do on ${what}? (required)` : `Note for ${what} (optional)`}
      </p>
      <NoteBox value={note} onChange={(e) => setNote(e.target.value)} autoFocus placeholder={cur.state === 'job' ? 'Work performed' : 'What did you do?'} />
      <p className="my-2 text-base text-slate-600">{cur.state === 'job' ? 'This is saved as a Work Performed note on the job and can’t be changed afterwards.' : 'Saved with your time.'}</p>

      {target ? (
        <Big tone="go" className="w-full" disabled={!ok} onClick={() => toJob(target)}>Clock out of {what} and into #{target.number}</Big>
      ) : picking ? (
        <div className="grid gap-2">
          <p className="text-lg font-semibold">Clock into:</p>
          {others.map((j) => (
            <Big key={j.id} disabled={!ok} className="text-left" onClick={() => toJob(j)}>#{j.number} · {j.customer.name}</Big>
          ))}
          {otherCats.map((c) => (
            <Big key={c.key} tone="light" disabled={!ok} className="border-2 border-slate-300 text-left" onClick={() => toTime(c.key, c.label)}>{c.label}</Big>
          ))}
          {others.length === 0 && otherCats.length === 0 && <p className="text-lg text-slate-600">No other jobs to clock into.</p>}
          <Big tone="light" className="border-2 border-slate-300" onClick={() => setPicking(false)}>Back</Big>
        </div>
      ) : (
        <div className="grid gap-3">
          <Big tone="go" disabled={!ok} onClick={() => setPicking(true)}>Clock into another job</Big>
          <Big tone="warn" disabled={!ok} onClick={() => out('LUNCH')}>Out for Lunch</Big>
          <Big tone="stop" disabled={!ok} onClick={() => out('DAY')}>Out for the Day</Big>
        </div>
      )}
    </Sheet>
  );
}

/** My Jobs: what you're clocked into, and non-billable shop time. */
export function ClockCard({ payload, userId, onLocal }: { payload: JobsPayload; userId: number; onLocal: (fn: (p: JobsPayload) => JobsPayload) => void }) {
  const [sheet, setSheet] = useState(false);
  const cur = currentClock(payload);
  const cats = categoriesOf(payload);
  const since = cur.since ? new Date(cur.since).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  const start = async (key: string, label: string) => {
    await enqueueItem(userId, `Clock in: ${label}`, { type: 'time_in', category: key });
    onLocal((p) => afterClock(p, { kind: 'time', key, label }));
  };
  return (
    <div className="mb-4 rounded-2xl bg-white p-4 shadow">
      <p className={`text-xl font-bold ${cur.state === 'job' || cur.state === 'time' ? 'text-green-800' : 'text-slate-700'}`}>
        {cur.state === 'job' && `● Clocked in on #${cur.number}${since ? ` since ${since}` : ''}`}
        {cur.state === 'time' && `● On ${cur.categoryLabel}${since ? ` since ${since}` : ''}`}
        {cur.state === 'lunch' && 'Out for lunch'}
        {cur.state === 'out' && 'Not clocked in'}
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {(cur.state === 'job' || cur.state === 'time') && <Big tone="stop" onClick={() => setSheet(true)}>Clock Out…</Big>}
        {cur.state !== 'time' && cats.map((c) => (
          <Big key={c.key} tone="light" className="border-2 border-slate-300" onClick={() => (cur.state === 'job' ? setSheet(true) : start(c.key, c.label))}>
            Clock into {c.label}
          </Big>
        ))}
      </div>
      {sheet && <ClockOutSheet payload={payload} userId={userId} onClose={() => setSheet(false)} onLocal={onLocal} />}
    </div>
  );
}
